export const EMPTY_PENDING = Object.freeze({
  removedItemIds: [],
  replacements: {},
  quantities: {},
});

export const buildDisplayFromRecommendation = (rec) => ({
  image: rec.image,
  brand: rec.brand,
  model: rec.model,
  title: rec.title,
  price: rec.price,
  specifications: rec.specifications,
});

export const buildEditCartItems = (lineItems, destroyIds = []) => {
  const destroySet = new Set(destroyIds);
  return (lineItems || []).reduce((acc, item) => {
    if (item.id && item.quantity) {
      acc.push({
        id: item.id,
        quantity: item.quantity,
        ...(destroySet.has(item.id) && { _destroy: true }),
      });
    }
    return acc;
  }, []);
};

export const buildBatchedEditCartItems = (lineItems, pendingChanges = {}) => {
  const removedIds = pendingChanges.removedItemIds || [];
  const replacements = pendingChanges.replacements || {};
  const quantities = pendingChanges.quantities || {};
  const removedSet = new Set(removedIds);
  const replacedIds = new Set(Object.keys(replacements));

  const items = (lineItems || []).reduce((acc, item) => {
    if (!item.id) return acc;
    const overriddenQty = quantities[item.id];
    const finalQty =
      overriddenQty != null ? overriddenQty : item.quantity || 0;
    const isDestroyed =
      removedSet.has(item.id) || replacedIds.has(String(item.id)) || finalQty <= 0;

    if (isDestroyed) {
      acc.push({
        id: item.id,
        quantity: item.quantity || 1,
        _destroy: true,
      });
    } else if (finalQty) {
      acc.push({ id: item.id, quantity: finalQty });
    }
    return acc;
  }, []);

  Object.values(replacements).forEach((replacement) => {
    if (replacement && replacement.variant_id) {
      items.push({
        variant_id: replacement.variant_id,
        quantity: replacement.quantity || 1,
      });
    }
  });

  return items;
};

export const computeVisibleCart = (lineItems, pendingChanges = {}) => {
  const removedIds = new Set(pendingChanges.removedItemIds || []);
  const replacements = pendingChanges.replacements || {};
  const quantities = pendingChanges.quantities || {};

  return (lineItems || [])
    .filter((item) => !removedIds.has(item.id))
    .filter((item) => {
      const q = quantities[item.id];
      return q == null || q > 0;
    })
    .map((item) => {
      const replacement = replacements[item.id];
      const overriddenQty = quantities[item.id];
      if (!replacement && overriddenQty == null) return item;
      const display = (replacement && replacement.display) || {};
      const baseQty =
        (replacement && replacement.quantity) || item.quantity || 1;
      return {
        ...item,
        ...display,
        id: item.id,
        quantity: overriddenQty != null ? overriddenQty : baseQty,
      };
    });
};

export const computeDisplayCart = (lineItems, pendingChanges = {}) => {
  const removedIds = new Set(pendingChanges.removedItemIds || []);
  const replacements = pendingChanges.replacements || {};
  const quantities = pendingChanges.quantities || {};

  return (lineItems || []).map((item) => {
    if (removedIds.has(item.id)) return { ...item, removed: true };
    const replacement = replacements[item.id];
    const overriddenQty = quantities[item.id];
    if (!replacement && overriddenQty == null) return { ...item, removed: false };
    const display = (replacement && replacement.display) || {};
    const baseQty =
      (replacement && replacement.quantity) || item.quantity || 1;
    return {
      ...item,
      ...display,
      id: item.id,
      quantity: overriddenQty != null ? overriddenQty : baseQty,
      removed: false,
    };
  });
};

export const sumLineItemsTotal = (lineItems) =>
  (lineItems || []).reduce(
    (sum, item) =>
      sum + parseFloat(item.price || 0) * parseInt(item.quantity || 0, 10),
    0
  );

export const hasPendingChanges = (pending) => {
  if (!pending) return false;
  return (
    (pending.removedItemIds || []).length > 0 ||
    Object.keys(pending.replacements || {}).length > 0 ||
    Object.keys(pending.quantities || {}).length > 0
  );
};

export const addReplacement = (pending, itemId, recItem, originalQty) => {
  if (!itemId) return pending;
  const removedItemIds = (pending.removedItemIds || []).filter(
    (id) => id !== itemId
  );
  return {
    ...pending,
    removedItemIds,
    replacements: {
      ...pending.replacements,
      [itemId]: {
        variant_id: recItem.variant_id,
        quantity: recItem.quantity || originalQty,
        display: buildDisplayFromRecommendation(recItem),
      },
    },
  };
};

export const queueRemoveItem = (pending, itemId) => {
  if (!itemId) return pending;
  const removed = pending.removedItemIds || [];
  if (removed.includes(itemId)) return pending;
  const quantities = { ...(pending.quantities || {}) };
  delete quantities[itemId];
  return {
    ...pending,
    removedItemIds: [...removed, itemId],
    quantities,
  };
};

export const restoreItems = (pending, itemIds) => {
  const ids = itemIds || [];
  if (!ids.length) return pending;
  const replacements = { ...pending.replacements };
  const quantities = { ...(pending.quantities || {}) };
  ids.forEach((id) => {
    delete replacements[id];
    delete quantities[id];
  });
  return {
    ...pending,
    removedItemIds: (pending.removedItemIds || []).filter(
      (id) => !ids.includes(id)
    ),
    replacements,
    quantities,
  };
};
